import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { POPUP } from '..';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import ReactDOM from 'react-dom';
import { Card } from '~/ui/card/Card';
import { usePopup } from '~/training/sequence/templates/items/audio-popup/components/usePopup';
import closeIcon from '~/assets/icons/close.svg';
import { t } from 'ttag';
import { drillAnimation } from '~/ui/animation';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { TickMarkIcon } from '../audio-popup/components/TickMarkIcon';

interface Props {
  id?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  top?: number;
  left?: number;
  btnTitle?: string;
  metric?: string;
  isPopupCentered?: boolean;
  blocking?: boolean;
  btnSize?: number;
}

interface DrillBoxProps {
  btnSize?: number;
}

const CloseIcon = styled(closeIcon)`
  transform: rotate(180deg);
  color: ${p => p.theme.colors.black};
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 1;
  border: none;
  padding: 0;
  background: transparent;
  right: 20px;
`;

const DrillBoxAnimation = styled.div<DrillBoxProps>`
  width: ${p => (p?.btnSize ? `${p?.btnSize}px` : '85px')};
  height: ${p => (p?.btnSize ? `${p?.btnSize}px` : '85px')};
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  background-color: accentDark;
  border-radius: 50%;
  opacity: 0.5;
  z-index: 1;
`;

const seqSelector = (s: SequenceStore) =>
  select(s, 'addBlocker', 'removeBlocker');

const PopupButton = ({
  id,
  title,
  subtitle,
  body,
  btnTitle = '+',
  isPopupCentered,
  blocking = false,
  btnSize = 55
}: Props) => {
  const { open, setOpen, popupRef } = usePopup();
  const ref = useRef<HTMLDivElement>();
  const openerButtonRef = useRef<HTMLButtonElement>();
  const [status, setStatus] = useState<string>('inprogress');
  const [top, setTop] = useState<number>();
  const [left, setLeft] = useState<number>();
  const { addBlocker, removeBlocker } = useSequenceStore(seqSelector, shallow);

  useEffect(() => {
    if (blocking && id) {
      addBlocker(id);
    }

    return () => {
      if (blocking && id) {
        removeBlocker(id);
      }
    };
  }, []);

  useEffect(() => {
    if (!blocking) return;

    if (open && id) {
      removeBlocker(id);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setStatus('completed');

      const handler = () => {
        setOpen(false);
      };

      setTimeout(() => {
        document.addEventListener('click', handler);
      });

      return () => document.removeEventListener('click', handler);
    }
  }, [open, popupRef]);

  useEffect(() => {
    if (!open) return;

    function updatePosition() {
      const rect = ref?.current?.getBoundingClientRect();
      const popupRect = popupRef?.current?.getBoundingClientRect();
      if (!rect || !popupRect) return;
      setTimeout(() => {
        if (isPopupCentered) {
          setTop(window.innerHeight / 2 - popupRect.height / 2);
          setLeft((window.innerWidth - popupRect.width * 1.8) / 2);
        } else {
          setTop(rect.bottom);
          setLeft(rect.left + rect.width / 2 - popupRect.width / 2);
        }
      });
    }

    window.addEventListener('resize', updatePosition);
    updatePosition();

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [ref.current, popupRef.current, open]);

  const clickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(!open);
    if (!open) {
      openerButtonRef.current = event.currentTarget;
      setTimeout(() => {
        popupRef.current?.querySelector('button')?.focus();
      }, 0);
    } else {
      openerButtonRef.current = undefined;
    }
  };

  useEffect(() => {
    if (!open && openerButtonRef && openerButtonRef.current) {
      openerButtonRef?.current?.focus();
    }
  }, [open]);

  return (
    <x.div
      w={`${btnSize}px`}
      h={`${btnSize}px`}
      backgroundColor="rgba(255,255,255,0.2)"
      borderRadius="50%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="relative"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
    >
      <DrillBoxAnimation btnSize={btnSize} />

      <x.button
        onClick={clickHandler}
        backgroundColor="white"
        color="black"
        borderRadius="50%"
        w={`${btnSize - 18}px`}
        h={`${btnSize - 18}px`}
        display="flex"
        justifyContent="center"
        alignItems="center"
        overflow="hidden"
        aria-label={`Open ${title || subtitle || btnTitle}`}
        role="button"
        aria-haspopup="true"
        zIndex="2"
      >
        {status === 'completed' ? (
          <TickMarkIcon />
        ) : (
          <x.span
            style={
              btnTitle === '+'
                ? { fontSize: '40px' }
                : {
                    fontSize: '24px',
                    fontWeight: '700',
                    display: 'block',
                    marginTop: '-3px',
                    lineHeight: '20px'
                  }
            }
          >
            {btnTitle}
          </x.span>
        )}
      </x.button>
      {open &&
        ReactDOM.createPortal(
          <Card
            ref={popupRef}
            position="fixed"
            p="md"
            onClick={e => e.stopPropagation()}
            top={`${top && top + 20}px`}
            left={`${left && left + 100}px`}
            gap={''}
            autoFocus
          >
            <CloseButton
              onClick={() => setOpen(false)}
              aria-label={t`close`}
              role="button"
              autoFocus
            >
              <CloseIcon />
            </CloseButton>
            <div role="alert">
              <x.h5
                color="accentDark"
                dangerouslySetInnerHTML={{ __html: subtitle || '' }}
              />
              <x.h2
                p="15px 0px 15px 0px"
                dangerouslySetInnerHTML={{ __html: title || '' }}
              />
              <x.div
                maxWidth="300px"
                dangerouslySetInnerHTML={{ __html: body || '' }}
              />
            </div>
          </Card>,
          document.getElementById('root')!
        )}
    </x.div>
  );
};

export type PopupDefinition = ItemDefinition<typeof POPUP, Props>;

export class Popup extends Item<typeof POPUP, Props> {
  protected render({
    id,
    title,
    subtitle,
    body,
    top,
    left,
    btnTitle,
    metric = 'px',
    isPopupCentered,
    blocking = false,
    btnSize
  }: RenderProps<Props>) {
    return top && left ? (
      <x.div
        position="absolute"
        top={`${top}${metric}`}
        left={`${left}${metric}`}
      >
        <PopupButton
          id={id}
          title={title}
          body={body}
          subtitle={subtitle}
          btnTitle={btnTitle}
          isPopupCentered={isPopupCentered}
          blocking={blocking}
          btnSize={btnSize}
        />
      </x.div>
    ) : (
      <PopupButton
        id={id}
        title={title}
        body={body}
        subtitle={subtitle}
        btnTitle={btnTitle}
        isPopupCentered={isPopupCentered}
        blocking={blocking}
        btnSize={btnSize}
      />
    );
  }
}

registry[POPUP] = Popup;
